import {createStyles} from '@mantine/core';
import React from 'react';

const useStyles = createStyles((theme) => ({
  container: {
    margin: '0 14px',
    position: 'relative',
    height: 196,
    background: theme.colors.blue[1],
    borderRadius: 8,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  gradient: {
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(60% 70% at 50% 110%, rgba(120, 134, 255, 0.45) 0%, transparent 70%)',
  },
  card: {
    position: 'absolute',
    background: theme.white,
    borderRadius: 8,
    boxShadow: '0 10px 24px -8px rgba(15, 18, 40, 0.18), 0 2px 4px rgba(15, 18, 40, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.6)',
    overflow: 'hidden',
  },
  cardBack: {
    width: 160,
    height: 128,
    left: 'calc(50% - 100px)',
    top: 20,
    transform: 'rotate(-6deg)',
  },
  cardFront: {
    width: 172,
    height: 136,
    left: 'calc(50% - 56px)',
    top: 20,
    transform: 'rotate(5deg)',
    boxShadow: '0 14px 32px -10px rgba(15, 18, 40, 0.28), 0 3px 6px rgba(15, 18, 40, 0.08)',
  },
  cardInner: {
    width: '100%',
    height: '100%',
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  labelBack: {
    fontSize: 7,
    fontWeight: 500,
    color: theme.colors.gray[6],
    marginBottom: 1,
  },
  labelFront: {
    fontSize: 7,
    fontWeight: 600,
    color: theme.colors.dark[9],
    marginBottom: 2,
    letterSpacing: '-0.005em',
  },
}));

function SuggestedPlotsCalloutIllustration(): JSX.Element {
  const {classes, cx} = useStyles();
  return (
    <div className={classes.container}>
      <div className={classes.gradient} />
      <div className={cx(classes.card, classes.cardBack)}>
        <div className={classes.cardInner}>
          <div className={classes.labelBack}>In Vivo Efficacy: A53T Mice (28-day)</div>
          <svg viewBox="0 0 200 140" style={{width: '100%', flex: 1}}>
            <text x="50" y="10" textAnchor="middle" fontSize="6.5" fontWeight="600" fill="#1f2937">
              Rotarod (Day 28)
            </text>
            {/* Grid lines */}
            <line x1="16" y1="108" x2="92" y2="108" stroke="#eef0f4" strokeWidth="0.5" />
            <line x1="16" y1="88" x2="92" y2="88" stroke="#eef0f4" strokeWidth="0.5" />
            <line x1="16" y1="68" x2="92" y2="68" stroke="#eef0f4" strokeWidth="0.5" />
            <line x1="16" y1="48" x2="92" y2="48" stroke="#eef0f4" strokeWidth="0.5" />
            <line x1="16" y1="28" x2="92" y2="28" stroke="#eef0f4" strokeWidth="0.5" />
            {/* Axes */}
            <line x1="16" y1="108" x2="92" y2="108" stroke="#9ca3af" strokeWidth="0.6" />
            <line x1="16" y1="22" x2="16" y2="108" stroke="#9ca3af" strokeWidth="0.6" />
            {/* Veh bar */}
            <rect x="20" y="78" width="12" height="30" fill="#9ca3af" rx="0.5" />
            <line x1="26" y1="73" x2="26" y2="83" stroke="#374151" strokeWidth="0.6" />
            <line x1="23" y1="73" x2="29" y2="73" stroke="#374151" strokeWidth="0.6" />
            <line x1="23" y1="83" x2="29" y2="83" stroke="#374151" strokeWidth="0.6" />
            <text x="26" y="118" textAnchor="middle" fontSize="5.5" fill="#6b7280">
              Veh
            </text>
            {/* 10 bar */}
            <rect x="38" y="70" width="12" height="38" fill="#bfdbfe" rx="0.5" />
            <line x1="44" y1="65" x2="44" y2="75" stroke="#374151" strokeWidth="0.6" />
            <line x1="41" y1="65" x2="47" y2="65" stroke="#374151" strokeWidth="0.6" />
            <line x1="41" y1="75" x2="47" y2="75" stroke="#374151" strokeWidth="0.6" />
            <text x="44" y="118" textAnchor="middle" fontSize="5.5" fill="#6b7280">
              10
            </text>
            {/* 30 bar */}
            <rect x="56" y="52" width="12" height="56" fill="#3b82f6" rx="0.5" />
            <line x1="62" y1="47" x2="62" y2="57" stroke="#374151" strokeWidth="0.6" />
            <line x1="59" y1="47" x2="65" y2="47" stroke="#374151" strokeWidth="0.6" />
            <line x1="59" y1="57" x2="65" y2="57" stroke="#374151" strokeWidth="0.6" />
            <text x="62" y="44" textAnchor="middle" fontSize="5.5" fill="#374151" fontWeight="600">
              **
            </text>
            <text x="62" y="118" textAnchor="middle" fontSize="5.5" fill="#6b7280">
              30
            </text>
            {/* 100 bar */}
            <rect x="74" y="36" width="12" height="72" fill="#1e3a8a" rx="0.5" />
            <line x1="80" y1="31" x2="80" y2="41" stroke="#374151" strokeWidth="0.6" />
            <line x1="77" y1="31" x2="83" y2="31" stroke="#374151" strokeWidth="0.6" />
            <line x1="77" y1="41" x2="83" y2="41" stroke="#374151" strokeWidth="0.6" />
            <text x="80" y="28" textAnchor="middle" fontSize="5.5" fill="#374151" fontWeight="600">
              ***
            </text>
            <text x="80" y="118" textAnchor="middle" fontSize="5.5" fill="#6b7280">
              100
            </text>
            {/* Right panel: Open Field */}
            <text x="150" y="10" textAnchor="middle" fontSize="6.5" fontWeight="600" fill="#1f2937">
              Open Field (Day 28)
            </text>
            <line x1="116" y1="108" x2="192" y2="108" stroke="#eef0f4" strokeWidth="0.5" />
            <line x1="116" y1="88" x2="192" y2="88" stroke="#eef0f4" strokeWidth="0.5" />
            <line x1="116" y1="68" x2="192" y2="68" stroke="#eef0f4" strokeWidth="0.5" />
            <line x1="116" y1="48" x2="192" y2="48" stroke="#eef0f4" strokeWidth="0.5" />
            <line x1="116" y1="28" x2="192" y2="28" stroke="#eef0f4" strokeWidth="0.5" />
            <line x1="116" y1="108" x2="192" y2="108" stroke="#9ca3af" strokeWidth="0.6" />
            <line x1="116" y1="22" x2="116" y2="108" stroke="#9ca3af" strokeWidth="0.6" />
            {/* Veh */}
            <rect x="120" y="68" width="12" height="40" fill="#9ca3af" rx="0.5" />
            <line x1="126" y1="63" x2="126" y2="73" stroke="#374151" strokeWidth="0.6" />
            <line x1="123" y1="63" x2="129" y2="63" stroke="#374151" strokeWidth="0.6" />
            <line x1="123" y1="73" x2="129" y2="73" stroke="#374151" strokeWidth="0.6" />
            <text x="126" y="118" textAnchor="middle" fontSize="5.5" fill="#6b7280">
              Veh
            </text>
            {/* 10 */}
            <rect x="138" y="64" width="12" height="44" fill="#bfdbfe" rx="0.5" />
            <line x1="144" y1="59" x2="144" y2="69" stroke="#374151" strokeWidth="0.6" />
            <line x1="141" y1="59" x2="147" y2="59" stroke="#374151" strokeWidth="0.6" />
            <line x1="141" y1="69" x2="147" y2="69" stroke="#374151" strokeWidth="0.6" />
            <text x="144" y="118" textAnchor="middle" fontSize="5.5" fill="#6b7280">
              10
            </text>
            {/* 30 */}
            <rect x="156" y="50" width="12" height="58" fill="#3b82f6" rx="0.5" />
            <line x1="162" y1="45" x2="162" y2="55" stroke="#374151" strokeWidth="0.6" />
            <line x1="159" y1="45" x2="165" y2="45" stroke="#374151" strokeWidth="0.6" />
            <line x1="159" y1="55" x2="165" y2="55" stroke="#374151" strokeWidth="0.6" />
            <text x="162" y="42" textAnchor="middle" fontSize="5.5" fill="#374151" fontWeight="600">
              *
            </text>
            <text x="162" y="118" textAnchor="middle" fontSize="5.5" fill="#6b7280">
              30
            </text>
            {/* 100 */}
            <rect x="174" y="38" width="12" height="70" fill="#1e3a8a" rx="0.5" />
            <line x1="180" y1="33" x2="180" y2="43" stroke="#374151" strokeWidth="0.6" />
            <line x1="177" y1="33" x2="183" y2="33" stroke="#374151" strokeWidth="0.6" />
            <line x1="177" y1="43" x2="183" y2="43" stroke="#374151" strokeWidth="0.6" />
            <text x="180" y="30" textAnchor="middle" fontSize="5.5" fill="#374151" fontWeight="600">
              **
            </text>
            <text x="180" y="118" textAnchor="middle" fontSize="5.5" fill="#6b7280">
              100
            </text>
            {/* Divider */}
            <line
              x1="100"
              y1="14"
              x2="100"
              y2="112"
              stroke="#e5e7eb"
              strokeWidth="0.5"
              strokeDasharray="1.5 1.5"
            />
            <text x="100" y="132" textAnchor="middle" fontSize="5" fill="#6b7280" fontStyle="italic">
              Linear trend: Rotarod p&lt;0.001 · Open Field p=0.002
            </text>
          </svg>
        </div>
      </div>
      <div className={cx(classes.card, classes.cardFront)}>
        <div className={classes.cardInner}>
          <div className={classes.labelFront}>Dose-Response: BNL-4783-08 inhibition across cell systems</div>
          <svg viewBox="0 0 200 140" style={{width: '100%', flex: 1}}>
            {/* Y-axis labels */}
            <text x="20" y="120" textAnchor="end" fontSize="5.5" fill="#6b7280">
              0
            </text>
            <text x="20" y="99" textAnchor="end" fontSize="5.5" fill="#6b7280">
              20
            </text>
            <text x="20" y="78" textAnchor="end" fontSize="5.5" fill="#6b7280">
              40
            </text>
            <text x="20" y="57" textAnchor="end" fontSize="5.5" fill="#6b7280">
              60
            </text>
            <text x="20" y="36" textAnchor="end" fontSize="5.5" fill="#6b7280">
              80
            </text>
            <text x="20" y="15" textAnchor="end" fontSize="5.5" fill="#6b7280">
              100
            </text>
            {/* X-axis labels */}
            <text x="22" y="127" textAnchor="middle" fontSize="5.5" fill="#6b7280">
              0.01
            </text>
            <text x="65" y="127" textAnchor="middle" fontSize="5.5" fill="#6b7280">
              0.1
            </text>
            <text x="109" y="127" textAnchor="middle" fontSize="5.5" fill="#6b7280">
              1
            </text>
            <text x="152" y="127" textAnchor="middle" fontSize="5.5" fill="#6b7280">
              10
            </text>
            <text x="196" y="127" textAnchor="middle" fontSize="5.5" fill="#6b7280">
              100
            </text>
            {/* Grid */}
            <line x1="22" y1="118" x2="196" y2="118" stroke="#eef0f4" strokeWidth="0.4" />
            <line x1="22" y1="97" x2="196" y2="97" stroke="#eef0f4" strokeWidth="0.4" />
            <line x1="22" y1="76" x2="196" y2="76" stroke="#eef0f4" strokeWidth="0.4" />
            <line x1="22" y1="55" x2="196" y2="55" stroke="#eef0f4" strokeWidth="0.4" />
            <line x1="22" y1="34" x2="196" y2="34" stroke="#eef0f4" strokeWidth="0.4" />
            <line x1="22" y1="13" x2="196" y2="13" stroke="#eef0f4" strokeWidth="0.4" />
            {/* Vertical grid */}
            <line x1="22" y1="12" x2="22" y2="118" stroke="#e5e7eb" strokeWidth="0.4" />
            <line x1="65" y1="12" x2="65" y2="118" stroke="#e5e7eb" strokeWidth="0.4" />
            <line x1="109" y1="12" x2="109" y2="118" stroke="#e5e7eb" strokeWidth="0.4" />
            <line x1="152" y1="12" x2="152" y2="118" stroke="#e5e7eb" strokeWidth="0.4" />
            <line x1="196" y1="12" x2="196" y2="118" stroke="#e5e7eb" strokeWidth="0.4" />
            {/* IC50 line */}
            <line x1="22" y1="65" x2="196" y2="65" stroke="#9ca3af" strokeWidth="0.4" strokeDasharray="2 2" />
            <text x="26" y="63" fontSize="4.5" fill="#9ca3af">
              50% (IC₅₀)
            </text>
            {/* Axes */}
            <line x1="22" y1="12" x2="22" y2="118" stroke="#9ca3af" strokeWidth="0.6" />
            <line x1="22" y1="118" x2="196" y2="118" stroke="#9ca3af" strokeWidth="0.6" />
            {/* Axis titles */}
            <text x="8" y="65" fontSize="5.5" fill="#374151" textAnchor="middle" transform="rotate(-90 8 65)">
              % Inhibition
            </text>
            <text x="109" y="138" fontSize="5.5" fill="#374151" textAnchor="middle">
              Concentration (µM)
            </text>
            {/* Blue curve (SH-SY5Y) */}
            <path
              d="M22,13 C35,14 49,16 65,28 C81,47 93,65 109,90 C120,102 136,112 152,116 C168,117 185,118 196,118"
              fill="none"
              stroke="#3B82F6"
              strokeWidth="1.4"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {/* Orange curve (iPSC) */}
            <path
              d="M22,12 C42,12 60,13 76,18 C92,28 104,47 114,56 C126,73 140,96 152,106 C168,113 185,117 196,117"
              fill="none"
              stroke="#F59E0B"
              strokeWidth="1.4"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {/* Green curve (Rat Cortical) */}
            <path
              d="M22,12 C45,12 65,14 80,19 C97,30 109,47 120,57 C133,75 148,100 158,107 C172,114 188,117 196,117"
              fill="none"
              stroke="#10B981"
              strokeWidth="1.4"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {/* Legend */}
            <circle cx="30" cy="132" r="2" fill="#3B82F6" />
            <text x="34" y="133.5" fontSize="4.5" fill="#374151">
              SH-SY5Y (IC₅₀ = 0.42 µM)
            </text>
            <circle cx="95" cy="132" r="2" fill="#F59E0B" />
            <text x="99" y="133.5" fontSize="4.5" fill="#374151">
              iPSC (IC₅₀ = 1.8 µM)
            </text>
            <circle cx="148" cy="132" r="2" fill="#10B981" />
            <text x="152" y="133.5" fontSize="4.5" fill="#374151">
              Rat Cortical (IC₅₀ = 2.3 µM)
            </text>
          </svg>
        </div>
      </div>
    </div>
  );
}

// Memoized: this is a large, static, propless illustration, so it only needs to render once.
export default React.memo(SuggestedPlotsCalloutIllustration);
